/**
 * Keeps each page box's cluster of visitors centred on its own box, whatever the Page Cluster Spacing (Chris, 2026-10-24: "when I change the cluster spacing it pushes the clusters' centre points down a little").
 *
 * WHY THEY DRIFT (measured, headless d3-force, six boxes, 300 ticks): the repulsion between bubbles ("charge") is ONE force across the whole board, so a cluster is pushed by its NEIGHBOURS' clusters as well as by its own
 * members. Each box's bubbles are pulled to its centre by a spring, and the spring only balances that push after the cluster has moved off-centre by (push / spring). The push grows with the spacing, so the offset does:
 * 0.1 px at spacing 1, 4 px at 30, 12.9 px at 100, in the direction of whichever neighbours are crowded. One cluster alone never drifts (its own repulsion is internal).
 *
 * THE FIX is a per-cluster centring force (d3's forceCenter, once for each box): each tick the bubbles that are SETTLED IN a box (within SETTLED_PX of its centre) are moved together so their average sits on the centre.
 * Bubbles still travelling in or out are left alone (they are not part of the cluster yet), and only the cluster's own average is corrected, so the spacing BETWEEN its bubbles is untouched.
 */
export interface RecentreNode {
  x?: number;
  y?: number;
}

export const SETTLED_PX = 110;
const RECENTRE_STEP = 0.3;

export const clusterRecentreForce = <N extends RecentreNode>(targetOf: (node: N) => { x: number; y: number; key: string | number } | null | undefined) => {
  // (a holder, not `let nodes: N[]` reassigned: this build's Flow-strip step cannot compile a TS file that does that)
  const held = { nodes: [] as N[] };
  const force = () => {
    const groups = new Map<string | number, { x: number; y: number; sx: number; sy: number; members: N[] }>();
    for (const node of held.nodes) {
      const target = targetOf(node);
      if (!target || typeof node.x !== "number" || typeof node.y !== "number") continue;
      if (Math.hypot(node.x - target.x, node.y - target.y) > SETTLED_PX) continue;
      let group = groups.get(target.key);
      if (!group) groups.set(target.key, (group = { x: target.x, y: target.y, sx: 0, sy: 0, members: [] }));
      group.sx += node.x;
      group.sy += node.y;
      group.members.push(node);
    }
    groups.forEach((group) => {
      const offsetX = group.sx / group.members.length - group.x;
      const offsetY = group.sy / group.members.length - group.y;
      if (Math.abs(offsetX) < 0.01 && Math.abs(offsetY) < 0.01) return;
      for (const member of group.members) {
        member.x = (member.x as number) - offsetX * RECENTRE_STEP;
        member.y = (member.y as number) - offsetY * RECENTRE_STEP;
      }
    });
  };
  force.initialize = (next: N[]) => {
    held.nodes = next;
  };

  return force;
};
